import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useAuth from '../auth/useAuth';
import { SortableTh, useSortedRows } from '../components/DataTable';
import Loading from '../components/Loading';
import { listSuppliers } from '../services/suppliers';
import { listPlants } from '../services/plants';
import { listTeam } from '../services/team';
import {
  COVERAGE_TYPE_LABELS,
  DAY_KEYS,
  listTimesheets,
} from '../services/timesheets';
import { aggregateBilling, presetRange } from '../services/billing';
import { scopedPlantCodes } from '../utils/filterScope';

/**
 * Billing — read-only dashboard rolling up approved timesheets into
 * supplier / plant totals for a picked date range. Labor is priced
 * per-day using each supplier's rate history, so a period that spans a
 * mid-month rate change bills correctly on each side. Mileage reuses
 * the IRS standard rate from config; expenses are passed through as
 * stored.
 *
 * The page is Director-only — the rolled-up numbers include every
 * supplier across the company and the dollar totals shouldn't float
 * out to RMs. RMs who want to sanity-check an individual timesheet
 * still have the approval inbox and the submitter's own timesheet
 * page.
 */
export default function Billing() {
  const { isDirector } = useAuth();

  // Date range — defaults to This Month so the page lands on the
  // in-flight period; the Last Month preset is one click away for the
  // post-close billing run. Quick presets below update both dates at
  // once. `activePreset` mirrors the currently-selected preset so the
  // matching button can render in its selected (orange primary) state;
  // hand-editing either date picker clears it.
  const defaultRange = useMemo(() => presetRange('this-month'), []);
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [activePreset, setActivePreset] = useState('this-month');

  // Plant / Supplier / Regional Manager / Submitted By filters —
  // same four narrows the Timesheet Approvals page uses, applied
  // upstream of aggregateBilling so the dashboard totals (Hours,
  // Mileage, Expenses, Billing Total, Plants Total) move with the
  // selected scope. Row-based filters (plant, supplier) keep
  // rowless adjustments (credits) visible regardless so they don't
  // silently disappear from the Adjustments callout while a filter
  // is on.
  const [plantFilter, setPlantFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [rmFilter, setRmFilter] = useState('');
  const [submitterFilter, setSubmitterFilter] = useState('');

  const [timesheets, setTimesheets] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [plants, setPlants] = useState([]);
  // Full team roster — used by the aggregator to resolve each
  // submitter's liaison manager(s) for the By Customer Liaison
  // Manager breakdown. Kept in page state so a "Refresh" reload
  // picks up any roster edits (new LM assignments) without a full
  // page reload.
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tsRows, sRows, pRows, tRows] = await Promise.all([
        listTimesheets(),
        listSuppliers(),
        listPlants(),
        listTeam(),
      ]);
      setTimesheets(tsRows || []);
      setSuppliers(sRows || []);
      setPlants(pRows || []);
      setTeam(tRows || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Filter predicates ----------------------------------------------
  //
  // Identity filters (RM, Submitter) apply at the timesheet level —
  // the whole row passes or fails together. Row-based filters
  // (plant, supplier) are applied INSIDE the timesheet by narrowing
  // its rows array to only matching rows, so the breakdown tables
  // ("By Supplier", "By Plant", "By Regional Manager", "By Team
  // Member") only count contributions that survive the filter.
  // Without that row-level narrowing a multi-plant timesheet would
  // still drag its OAC row into "By Plant" even when the user
  // filtered to SHAP, which felt like the filter wasn't applying.
  const matchesRm = useCallback(
    (t) => {
      if (!rmFilter) return true;
      const target = rmFilter.trim().toLowerCase();
      const stored = (t.RegionalManager || '').trim().toLowerCase();
      if (target === 'unassigned') return stored === '';
      return stored === target;
    },
    [rmFilter],
  );
  const submitterKey = useCallback((t) => {
    const id = String(t?.SubmitterId || '').trim();
    if (id) return id;
    return (t?.SubmitterName || '').trim().toLowerCase();
  }, []);
  const matchesSubmitter = useCallback(
    (t) => {
      if (!submitterFilter) return true;
      return submitterKey(t) === submitterFilter;
    },
    [submitterFilter, submitterKey],
  );

  // Timesheets after every user filter, ready to feed into
  // aggregateBilling. Identity filters drop whole timesheets;
  // plant/supplier filters narrow rows-within-timesheet so the
  // aggregator's per-row math only counts matching rows. Rowless
  // adjustments (credits) pass through unchanged so they remain
  // visible in the Adjustments callout. Memoized so both the
  // aggregated totals and the callout stay perfectly in sync.
  const filteredTimesheets = useMemo(() => {
    const plantTarget = plantFilter ? plantFilter.toUpperCase() : '';
    const supplierTarget = supplierFilter ? supplierFilter.toUpperCase() : '';
    const rowBasedActive = Boolean(plantTarget || supplierTarget);
    const list = (timesheets || []).filter(matchesRm).filter(matchesSubmitter);
    if (!rowBasedActive) return list;
    const out = [];
    for (const t of list) {
      const rows = Array.isArray(t.rows) ? t.rows : [];
      if (rows.length === 0) {
        // Rowless adjustments (credits) — keep them so the
        // Adjustments callout doesn't silently drop them when the
        // filter is on.
        if (t.IsAdjustment) out.push(t);
        continue;
      }
      const matchingRows = rows.filter((r) => {
        const rPlant = String(r.plantCode || '').trim().toUpperCase();
        const rSupplier = String(r.supplierCode || '').trim().toUpperCase();
        if (plantTarget && rPlant !== plantTarget) return false;
        if (supplierTarget && rSupplier !== supplierTarget) return false;
        return true;
      });
      if (matchingRows.length === 0) continue;
      out.push({ ...t, rows: matchingRows });
    }
    return out;
  }, [
    timesheets,
    plantFilter,
    supplierFilter,
    matchesRm,
    matchesSubmitter,
  ]);

  // Dropdown option lists — built from the loaded data so the user
  // only sees plants/suppliers/RMs/submitters who actually have
  // billing-relevant rows in play.
  const rmOptions = useMemo(() => {
    const set = new Set();
    for (const t of timesheets || []) {
      const rm = (t.RegionalManager || '').trim();
      if (rm) set.add(rm);
      else set.add('Unassigned');
    }
    return Array.from(set).sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
  }, [timesheets]);
  const submitterOptions = useMemo(() => {
    const map = new Map();
    for (const t of timesheets || []) {
      const key = submitterKey(t);
      if (!key) continue;
      if (!map.has(key)) {
        map.set(key, {
          value: key,
          label: (t.SubmitterName || '').trim() || '(unnamed)',
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
    );
  }, [timesheets, submitterKey]);

  // When a specific person is chosen in "Submitted By" or "Regional Manager",
  // limit the Plant dropdown to that person's assigned plant(s) — and if they
  // have exactly one, select it automatically (per Jillian, Sep 2026).
  const plantScope = useMemo(
    () =>
      scopedPlantCodes(team, {
        submitterKey: submitterFilter,
        rmName: rmFilter,
      }),
    [team, submitterFilter, rmFilter],
  );
  useEffect(() => {
    if (!plantScope) return; // no person selected → leave Plant alone
    setPlantFilter((prev) => {
      if (plantScope.size === 1) return [...plantScope][0];
      if (prev && !plantScope.has(String(prev).toUpperCase())) return '';
      return prev;
    });
  }, [plantScope]);

  const billing = useMemo(
    () =>
      aggregateBilling(
        filteredTimesheets,
        suppliers,
        { startDate, endDate },
        team,
      ),
    [filteredTimesheets, suppliers, startDate, endDate, team],
  );

  // Adjustments visible in the current range — approved timesheets
  // flagged IsAdjustment whose WeekStart falls in [start, end].
  // Split into two parallel lists so the callout can render
  // Supplements (which already add into the aggregated totals above)
  // and Credits (which don't — credits are flat dollar reductions
  // captured separately) as distinct sub-tables. Each list is
  // sorted by submitter then week so it reads predictably.
  const { supplementsInPeriod, creditsInPeriod, creditsTotal } = useMemo(() => {
    const isoStart = startDate || '';
    const isoEnd = endDate || '';
    const sortAdj = (a, b) => {
      const an = (a.SubmitterName || '').toLowerCase();
      const bn = (b.SubmitterName || '').toLowerCase();
      if (an !== bn) return an.localeCompare(bn);
      return (a.WeekStart || '').localeCompare(b.WeekStart || '');
    };
    const approvedAdjustments = (filteredTimesheets || [])
      .filter((t) => t.IsAdjustment && t.Status === 'Approved')
      .filter((t) => {
        const ws = (t.WeekStart || '').slice(0, 10);
        if (!ws) return false;
        if (isoStart && ws < isoStart) return false;
        if (isoEnd && ws > isoEnd) return false;
        return true;
      });
    const supplements = approvedAdjustments
      .filter((t) => t.IsSupplement)
      .sort(sortAdj);
    const credits = approvedAdjustments
      .filter((t) => t.IsCredit)
      .sort(sortAdj);
    const total = credits.reduce(
      (acc, t) => acc + (Number(t.CreditAmount) || 0),
      0,
    );
    return {
      supplementsInPeriod: supplements,
      creditsInPeriod: credits,
      creditsTotal: total,
    };
  }, [filteredTimesheets, startDate, endDate]);

  // ---- Unusual-hours detection ---------------------------------------
  //
  // For each plant, compare labor hours inside the selected range
  // against that plant's "usual" pace — its average over the 8 weeks
  // immediately before the range, scaled to the same number of days.
  // The comparison window is clamped to today so an in-progress month
  // doesn't read as "lower than usual" just because it isn't over.
  // Plants whose actual hours land 25%+ above or below the expected
  // figure get flagged. Plants with a tiny baseline (< 5 expected
  // hours) are skipped — percentage swings on near-zero numbers are
  // noise, not signal. Respects the same Plant / Supplier / RM /
  // Submitted By filters as everything else on the page.
  const unusualHoursPlants = useMemo(() => {
    const isoStart = (startDate || '').slice(0, 10);
    const isoEnd = (endDate || '').slice(0, 10);
    if (!isoStart || !isoEnd || isoStart > isoEnd) return [];
    const todayIso = new Date().toISOString().slice(0, 10);
    const effectiveEnd = isoEnd > todayIso ? todayIso : isoEnd;
    if (effectiveEnd < isoStart) return [];

    const periodDays = daysBetweenIso(isoStart, effectiveEnd) + 1;
    const baselineEnd = addDaysIso(isoStart, -1);
    const baselineStart = addDaysIso(isoStart, -56);
    const BASELINE_DAYS = 56;

    const actualByPlant = plantLaborHoursInWindow(
      filteredTimesheets,
      isoStart,
      effectiveEnd,
    );
    const baselineByPlant = plantLaborHoursInWindow(
      filteredTimesheets,
      baselineStart,
      baselineEnd,
    );

    const out = [];
    const allPlants = new Set([
      ...actualByPlant.keys(),
      ...baselineByPlant.keys(),
    ]);
    for (const plantCode of allPlants) {
      const actual = actualByPlant.get(plantCode) || 0;
      const baseline = baselineByPlant.get(plantCode) || 0;
      const expected = (baseline * periodDays) / BASELINE_DAYS;
      if (expected < 5) continue; // too little history to judge
      const pct = ((actual - expected) / expected) * 100;
      if (Math.abs(pct) < 25) continue;
      out.push({
        plantCode,
        actual: Math.round(actual * 100) / 100,
        expected: Math.round(expected * 100) / 100,
        pct: Math.round(pct),
      });
    }
    // Biggest swings first.
    return out.sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct));
  }, [filteredTimesheets, startDate, endDate]);

  if (!isDirector) {
    return (
      <div className="card">
        <div className="card-title">Billing</div>
        <p className="muted">
          Only Directors can view the Billing dashboard. If you need a
          supplier's current totals, ask a Director to pull them up.
        </p>
      </div>
    );
  }
  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
        <div className="card-title">Couldn't Load Billing</div>
        <p>{error.message || 'Unknown error.'}</p>
        <button className="btn btn-outline btn-sm" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  const applyPreset = (preset) => {
    const { startDate: s, endDate: e } = presetRange(preset);
    setStartDate(s);
    setEndDate(e);
    setActivePreset(preset);
  };

  const hasAnyBilling = billing.suppliers.length > 0;

  return (
    <>
      <div className="page-header">
        <h1>Billing Manager</h1>
        <div className="actions">
          <button className="btn btn-outline" onClick={load}>
            Refresh
          </button>
        </div>
      </div>

      {/* Range picker card — quick presets on one line, explicit
          date pickers below for when the preset doesn't match. */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          {/* Preset order matches the Timesheet Approvals page for
              consistency: lead with the default (This Month), then
              current → previous within each pair, scaling from
              shorter to longer windows. */}
          <PresetButton
            label="This Month"
            onClick={() => applyPreset('this-month')}
            active={activePreset === 'this-month'}
          />
          <PresetButton
            label="Last Month"
            onClick={() => applyPreset('last-month')}
            active={activePreset === 'last-month'}
          />
          <PresetButton
            label="This Week"
            onClick={() => applyPreset('this-week')}
            active={activePreset === 'this-week'}
          />
          <PresetButton
            label="Last Week"
            onClick={() => applyPreset('last-week')}
            active={activePreset === 'last-week'}
          />
          <PresetButton
            label="Past 4 Weeks"
            onClick={() => applyPreset('past-4-weeks')}
            active={activePreset === 'past-4-weeks'}
          />
          <PresetButton
            label="Past 5 Weeks"
            onClick={() => applyPreset('past-5-weeks')}
            active={activePreset === 'past-5-weeks'}
          />
          <PresetButton
            label="Year to Date"
            onClick={() => applyPreset('ytd')}
            active={activePreset === 'ytd'}
          />
          <PresetButton
            label="Last Year"
            onClick={() => applyPreset('last-year')}
            active={activePreset === 'last-year'}
          />
        </div>
        <div
          style={{
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--brand-muted)',
              }}
            >
              From
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setActivePreset(null);
              }}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--brand-border)',
                borderRadius: 6,
                fontSize: 14,
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--brand-muted)',
              }}
            >
              To
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setActivePreset(null);
              }}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--brand-border)',
                borderRadius: 6,
                fontSize: 14,
              }}
            />
          </div>
          <div
            className="muted"
            style={{ marginLeft: 'auto', fontSize: 13, alignSelf: 'center' }}
          >
            {formatRangeLabel(startDate, endDate)}
          </div>
        </div>

        {/* Plant / Supplier / Regional Manager / Submitted By filters
            — same four narrows the Timesheet Approvals page uses,
            applied upstream of the billing aggregation so the totals
            tiles and every breakdown below update live. Each filter
            stacks with the date range and with the others. Credits
            (rowless) stay visible regardless of plant or supplier
            picks. */}
        <div
          style={{
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            marginTop: 12,
            borderTop: '1px solid var(--brand-border, #e5e7eb)',
            paddingTop: 12,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--brand-muted)',
              }}
            >
              Plant
            </label>
            <select
              value={plantFilter}
              onChange={(e) => setPlantFilter(e.target.value)}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--brand-border)',
                borderRadius: 6,
                fontSize: 14,
                minWidth: 240,
              }}
            >
              <option value="">All plants</option>
              {plants
                .filter((p) => p.PlantCode)
                .filter(
                  (p) =>
                    !plantScope ||
                    plantScope.has(String(p.PlantCode).toUpperCase()),
                )
                .slice()
                .sort((a, b) =>
                  String(a.PlantCode).localeCompare(String(b.PlantCode)),
                )
                .map((p) => (
                  <option key={p.PlantCode} value={p.PlantCode}>
                    {p.Title ? `${p.PlantCode} — ${p.Title}` : p.PlantCode}
                  </option>
                ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--brand-muted)',
              }}
            >
              Supplier
            </label>
            <select
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--brand-border)',
                borderRadius: 6,
                fontSize: 14,
                minWidth: 240,
              }}
            >
              <option value="">All suppliers</option>
              {suppliers
                .filter((s) => s.SupplierCode)
                .slice()
                .sort((a, b) => {
                  const an = (a.Title || a.SupplierCode || '').toLowerCase();
                  const bn = (b.Title || b.SupplierCode || '').toLowerCase();
                  return an.localeCompare(bn);
                })
                .map((s) => (
                  <option key={s.SupplierCode} value={s.SupplierCode}>
                    {s.Title
                      ? `${s.SupplierCode} — ${s.Title}`
                      : s.SupplierCode}
                  </option>
                ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--brand-muted)',
              }}
            >
              Regional Manager
            </label>
            <select
              value={rmFilter}
              onChange={(e) => setRmFilter(e.target.value)}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--brand-border)',
                borderRadius: 6,
                fontSize: 14,
                minWidth: 220,
              }}
            >
              <option value="">All Regional Managers</option>
              {rmOptions.map((rm) => (
                <option key={rm} value={rm}>
                  {rm}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--brand-muted)',
              }}
            >
              Submitted By
            </label>
            <select
              value={submitterFilter}
              onChange={(e) => setSubmitterFilter(e.target.value)}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--brand-border)',
                borderRadius: 6,
                fontSize: 14,
                minWidth: 220,
              }}
            >
              <option value="">All submitters</option>
              {submitterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {(plantFilter ||
            supplierFilter ||
            rmFilter ||
            submitterFilter) && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => {
                setPlantFilter('');
                setSupplierFilter('');
                setRmFilter('');
                setSubmitterFilter('');
              }}
              style={{ alignSelf: 'flex-end', marginBottom: 1 }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Totals section — three headline tiles side by side so the
          director can see the period's plants, hours, and dollars at
          a glance. Section heading matches the other Billing section
          headings below for visual parity. */}
      <div style={{ marginTop: 24 }}>
        <SectionHeading>Totals</SectionHeading>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 14,
          }}
        >
          <TotalTile
            label="Plants Total"
            value={`${countUniquePlants(billing)}`}
            detail={`of ${plants.length} plants`}
          />
          <TotalTile
            label="Hours Total"
            value={fmtNum(billing.grandTotalHours)}
            unit="hrs"
            detail={hoursTotalDetail(billing)}
          />
          <TotalTile
            label="Expenses Total"
            value={fmtMoney(billing.grandTotalExpenseDollars)}
          />
          <TotalTile
            label="Mileage Total"
            value={fmtMoney(billing.grandTotalMileageDollars)}
            detail={
              billing.grandTotalMiles > 0
                ? `${fmtNum(billing.grandTotalMiles)} mi`
                : undefined
            }
          />
          <TotalTile
            label="Billing Total"
            value={fmtMoney(billing.grandTotal)}
            detail={periodTotalDetail(billing)}
            highlighted
          />
        </div>
      </div>

      {/* Adjustments callout — surfaces approved supplements and
          credits whose WeekStart falls in the selected range. Two
          sub-tables because the math goes opposite directions:
          supplements ADD to the customer bill and are already
          rolled into the totals above; credits SUBTRACT and are
          tracked separately as flat dollar amounts (the totals
          above don't auto-subtract them yet — that lands when the
          Invoices project ships). The Director's BillingDisposition
          on each row tells the invoice flow how to handle it: a
          supplemental-invoice supplement needs its own invoice
          referencing the original; a next-invoice supplement is a
          PPA line on the next regular invoice; a credit-memo
          credit gets a formal CR-#### document; a
          next-invoice-credit credit is a deduction line on the next
          regular invoice. */}
      {(supplementsInPeriod.length > 0 || creditsInPeriod.length > 0) && (
        <div style={{ marginTop: 24 }}>
          <SectionHeading>Adjustments in this period</SectionHeading>
          {supplementsInPeriod.length > 0 && (
            <div
              className="card"
              style={{
                borderColor: 'var(--brand-primary, #E87722)',
                background: 'var(--brand-primary-soft, #fff4e6)',
                marginBottom: creditsInPeriod.length > 0 ? 12 : 0,
              }}
            >
              <p
                style={{
                  margin: '0 0 12px',
                  fontSize: 13,
                  color: 'var(--brand-primary-dark, #b14a14)',
                }}
              >
                <strong>
                  {supplementsInPeriod.length}{' '}
                  supplement{supplementsInPeriod.length === 1 ? '' : 's'}
                </strong>{' '}
                added against approved timesheets in this period. Their
                hours and dollars are already included in the totals
                above — this list exists so the invoice flow honors
                each chosen billing disposition.
              </p>
              <table className="mini-table" style={{ background: '#fff' }}>
                <thead>
                  <tr>
                    <th>Submitter</th>
                    <th>Week</th>
                    <th>Reason</th>
                    <th>Billing</th>
                    <th style={{ width: 60 }}> </th>
                  </tr>
                </thead>
                <tbody>
                  {supplementsInPeriod.map((t) => (
                    <tr key={t.id}>
                      <td>{t.SubmitterName || '—'}</td>
                      <td>
                        {t.WeekStart ? (
                          <span>{t.WeekStart}</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        {t.AdjustmentReason ||
                          t.SupplementReason || (
                            <span className="muted">no reason on file</span>
                          )}
                      </td>
                      <td>
                        {t.BillingDisposition === 'supplemental-invoice' ? (
                          <span>Supplemental invoice</span>
                        ) : t.BillingDisposition === 'next-invoice' ? (
                          <span>Next invoice (PPA)</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <Link
                          to={`/timesheets/approvals?ts=${encodeURIComponent(t.id)}`}
                          title="Open this supplement on the Approvals tab"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {creditsInPeriod.length > 0 && (
            <div
              className="card"
              style={{
                borderColor: '#2563eb',
                background: '#eff6ff',
              }}
            >
              <p
                style={{
                  margin: '0 0 12px',
                  fontSize: 13,
                  color: '#1e3a8a',
                }}
              >
                <strong>
                  {creditsInPeriod.length}{' '}
                  credit{creditsInPeriod.length === 1 ? '' : 's'}
                </strong>{' '}
                totaling{' '}
                <strong>−${creditsTotal.toFixed(2)}</strong>{' '}
                issued against approved timesheets in this period.
                Credits are NOT automatically subtracted from the
                Billing Total above — the Invoices flow will apply
                each credit according to its disposition (credit memo
                or credit line on next invoice).
              </p>
              <table className="mini-table" style={{ background: '#fff' }}>
                <thead>
                  <tr>
                    <th>Submitter</th>
                    <th>Week</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Reason</th>
                    <th>Credit via</th>
                    <th style={{ width: 60 }}> </th>
                  </tr>
                </thead>
                <tbody>
                  {creditsInPeriod.map((t) => (
                    <tr key={t.id}>
                      <td>{t.SubmitterName || '—'}</td>
                      <td>
                        {t.WeekStart ? (
                          <span>{t.WeekStart}</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        −${(Number(t.CreditAmount) || 0).toFixed(2)}
                      </td>
                      <td>
                        {t.AdjustmentReason ||
                          t.SupplementReason || (
                            <span className="muted">no reason on file</span>
                          )}
                      </td>
                      <td>
                        {t.BillingDisposition === 'credit-memo' ? (
                          <span>Credit memo</span>
                        ) : t.BillingDisposition === 'next-invoice-credit' ? (
                          <span>Next invoice (credit line)</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <Link
                          to={`/timesheets/approvals?ts=${encodeURIComponent(t.id)}`}
                          title="Open this credit on the Approvals tab"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Missing-rate diagnostic. If any supplier/plant pair had
          approved hours logged but no rate on file, the director needs
          to know — those hours didn't contribute to the dollar totals
          above. Surface the pairs as warning tiles so it's a one-click
          fix on the Supplier record. */}
      {billing.missingRatePairs.length > 0 && (
        <div
          className="card"
          style={{
            borderColor: 'var(--brand-warning, #b45309)',
            background: '#fffbeb',
          }}
        >
          <div
            className="card-title"
            style={{ color: 'var(--brand-warning, #b45309)' }}
          >
            ⚠ Missing Rates
          </div>
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            These supplier / plant pairs have approved hours in this
            period but no hourly rate recorded. Add a rate on the
            supplier's Plant Locations &amp; Coverage record and refresh.
          </p>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '8px 0 0',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 13,
            }}
          >
            {billing.missingRatePairs.map((p) => (
              <li key={`${p.supplierCode}|${p.plantCode}`}>
                <Link
                  to={`/suppliers/${encodeURIComponent(p.supplierCode)}`}
                  style={{ color: 'var(--brand-primary)' }}
                >
                  {p.supplierCode}
                </Link>{' '}
                — <span className="badge plant">{p.plantCode}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Unbilled-Active diagnostic — every supplier/plant pair that
          the supplier record currently lists as Active should produce
          billable activity in the period. Anything missing means
          either a pending timesheet, an unapproved one, or a stale
          Active flag that should have moved to Paused/Closed. The
          card sits next to Missing Rates as a sister warning so the
          Director sees both classes of "something's off" together. */}
      {billing.unbilledActivePairs.length > 0 && (
        <div
          className="card"
          style={{
            borderColor: 'var(--brand-warning, #b45309)',
            background: '#fffbeb',
            marginTop: 14,
          }}
        >
          <div
            className="card-title"
            style={{ color: 'var(--brand-warning, #b45309)' }}
          >
            ⚠ Active Suppliers Not Billed
          </div>
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            These suppliers/plants are currently marked as Active but
            show no approved Billing Tracker activity for this period.
            Please check for any missing or unapproved Billing Trackers,
            or update the supplier status if coverage has ended or is
            paused.
          </p>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '8px 0 0',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 13,
            }}
          >
            {billing.unbilledActivePairs.map((p) => (
              <li key={`${p.supplierCode}|${p.plantCode}`}>
                <Link
                  to={`/suppliers/${encodeURIComponent(p.supplierCode)}`}
                  style={{ color: 'var(--brand-primary)' }}
                >
                  {p.supplierName}
                </Link>{' '}
                — <span className="badge plant">{p.plantCode}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Unusual-hours diagnostic — plants whose labor hours in this
          period run 25%+ above or below their own pace from the prior
          8 weeks. A spike can mean an unlogged scope change or a
          mis-keyed timesheet; a dip can mean missing timesheets. The
          comparison clamps to today so an in-progress period isn't
          flagged "low" just for being unfinished. */}
      {unusualHoursPlants.length > 0 && (
        <div
          className="card"
          style={{
            borderColor: 'var(--brand-warning, #b45309)',
            background: '#fffbeb',
            marginTop: 14,
          }}
        >
          <div
            className="card-title"
            style={{ color: 'var(--brand-warning, #b45309)' }}
          >
            ⚠ Plants With Unusual Hours
          </div>
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            These plants logged at least 25% more or fewer approved
            hours in this period than their usual pace (their average
            over the 8 weeks before the period, scaled to the same
            length). Worth a quick look — a spike can be a scope change
            or a mis-keyed entry; a dip can mean timesheets are missing
            or unapproved.
          </p>
          <table className="mini-table" style={{ background: '#fff' }}>
            <thead>
              <tr>
                <th style={{ width: 110 }}>Plant</th>
                <th style={{ textAlign: 'right' }}>Hours This Period</th>
                <th style={{ textAlign: 'right' }}>Usual Pace</th>
                <th style={{ textAlign: 'right', width: 140 }}>Difference</th>
              </tr>
            </thead>
            <tbody>
              {unusualHoursPlants.map((p) => (
                <tr key={p.plantCode}>
                  <td>
                    <Link
                      to={`/plants/${encodeURIComponent(p.plantCode)}`}
                      style={{ textDecoration: 'none' }}
                    >
                      <span className="badge plant">{p.plantCode}</span>
                    </Link>
                  </td>
                  <td style={{ textAlign: 'right' }}>{p.actual} hrs</td>
                  <td style={{ textAlign: 'right' }}>{p.expected} hrs</td>
                  <td
                    style={{
                      textAlign: 'right',
                      fontWeight: 700,
                      color:
                        p.pct > 0
                          ? 'var(--brand-danger)'
                          : '#2563eb',
                    }}
                  >
                    {p.pct > 0 ? `+${p.pct}% higher` : `${p.pct}% lower`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!hasAnyBilling ? (
        <div className="card">
          <p className="muted" style={{ marginTop: 0 }}>
            No approved Billing Tracker entries fall in this range. Pick a
            wider date range, or check the Billing Tracker Approvals page
            to see what's still pending.
          </p>
        </div>
      ) : (
        <>
          {/* Each subsection is collapsed by default (Jillian, Aug 2026);
              click the heading to expand. */}
          {/* Supplier Billing — the external-facing view: what we're
              charging each supplier for, organized by plant. */}
          <CollapsibleSection title="By Supplier">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}
            >
              {billing.suppliers.map((s) => (
                <SupplierBillingCard key={s.supplierCode} supplier={s} />
              ))}
            </div>
          </CollapsibleSection>

          {/* By Plant — same data, grouped by the plant where the work
              happened. One row per plant with rolled-up totals across
              every supplier and team member that worked there in the
              period. */}
          <CollapsibleSection title="By Plant">
            <PlantBillingTable rows={aggregateByPlant(billing)} />
          </CollapsibleSection>

          {/* By Regional Manager — rolled up across every submitter
              the RM oversees. */}
          <CollapsibleSection title="By Regional Manager">
            <RegionalManagerBillingTable rows={billing.regionalManagers} />
          </CollapsibleSection>

          {/* Proxemics Team Billing — the internal-facing view: who
              generated the billable work, now rolled up per team member
              (suppliers + plants combined) like the RM view. */}
          <CollapsibleSection title="By Team Member">
            <TeamBillingTable rows={aggregateTeamByMember(billing)} />
          </CollapsibleSection>
        </>
      )}
    </>
  );
}

// ---- By-plant table ----------------------------------------------

/**
 * Flatten the billing breakdown into a per-plant view. Each output row
 * rolls up every supplier that worked at that plant in the period.
 * Supplier list is comma-joined for compact display.
 */
function aggregateByPlant(billing) {
  const byPlant = new Map();
  for (const s of billing.suppliers || []) {
    for (const p of s.plants || []) {
      if (!byPlant.has(p.plantCode)) {
        byPlant.set(p.plantCode, {
          plantCode: p.plantCode,
          suppliers: [],
          laborHours: 0,
          laborDollars: 0,
          miles: 0,
          mileageDollars: 0,
          expenseDollars: 0,
          orphanHours: 0,
          total: 0,
        });
      }
      const bucket = byPlant.get(p.plantCode);
      if (!bucket.suppliers.includes(s.supplierName)) {
        bucket.suppliers.push(s.supplierName);
      }
      bucket.laborHours += p.laborHours;
      bucket.laborDollars += p.laborDollars;
      bucket.miles += p.miles;
      bucket.mileageDollars += p.mileageDollars;
      bucket.expenseDollars += p.expenseDollars;
      bucket.orphanHours += p.orphanHours;
      bucket.total += p.total;
    }
  }
  // Sort alphabetically by plant code for a stable order across renders.
  return [...byPlant.values()]
    .map((p) => ({
      ...p,
      // Round totals to cents so display math adds up cleanly.
      laborHours: Math.round(p.laborHours * 100) / 100,
      laborDollars: Math.round(p.laborDollars * 100) / 100,
      miles: Math.round(p.miles * 100) / 100,
      mileageDollars: Math.round(p.mileageDollars * 100) / 100,
      expenseDollars: Math.round(p.expenseDollars * 100) / 100,
      orphanHours: Math.round(p.orphanHours * 100) / 100,
      total: Math.round(p.total * 100) / 100,
      suppliers: [...p.suppliers].sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => a.plantCode.localeCompare(b.plantCode));
}

/**
 * Roll up the per-(team member, plant, supplier) billing rows into ONE row
 * per team member, combining their suppliers and plants into lists and
 * summing the dollars/hours — mirrors the By Regional Manager view so the
 * Team Member section isn't a long per-entry list (per Jillian, Aug 2026).
 */
function aggregateTeamByMember(billing) {
  const byMember = new Map();
  for (const r of billing.team || []) {
    const key = r.submitterKey || r.submitterName || '';
    if (!byMember.has(key)) {
      byMember.set(key, {
        submitterKey: key,
        submitterName: r.submitterName || key,
        supplierNames: [],
        plantCodes: [],
        laborHours: 0,
        expenseDollars: 0,
        mileageDollars: 0,
        miles: 0,
        orphanHours: 0,
        total: 0,
      });
    }
    const b = byMember.get(key);
    if (r.supplierName && !b.supplierNames.includes(r.supplierName)) {
      b.supplierNames.push(r.supplierName);
    }
    if (r.plantCode && !b.plantCodes.includes(r.plantCode)) {
      b.plantCodes.push(r.plantCode);
    }
    b.laborHours += Number(r.laborHours) || 0;
    b.expenseDollars += Number(r.expenseDollars) || 0;
    b.mileageDollars += Number(r.mileageDollars) || 0;
    b.miles += Number(r.miles) || 0;
    b.orphanHours += Number(r.orphanHours) || 0;
    b.total += Number(r.total) || 0;
  }
  return [...byMember.values()]
    .map((m) => ({
      ...m,
      laborHours: Math.round(m.laborHours * 100) / 100,
      expenseDollars: Math.round(m.expenseDollars * 100) / 100,
      mileageDollars: Math.round(m.mileageDollars * 100) / 100,
      miles: Math.round(m.miles * 100) / 100,
      orphanHours: Math.round(m.orphanHours * 100) / 100,
      total: Math.round(m.total * 100) / 100,
      supplierNames: [...m.supplierNames].sort((a, b) => a.localeCompare(b)),
      plantCodes: [...m.plantCodes].sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => (a.submitterName || '').localeCompare(b.submitterName || ''));
}

function PlantBillingTable({ rows }) {
  // Sort keys map to row fields (or derived values) — Total Mileage
  // sorts on raw dollars even though the cell shows "$N + N mi", and
  // Suppliers sorts on the joined supplier list as a string.
  const plantSortConfig = useMemo(
    () => ({
      plantCode: (r) => r.plantCode,
      suppliers: (r) => (r.suppliers || []).join(', '),
      laborHours: (r) => Number(r.laborHours) + Number(r.orphanHours || 0),
      expenseDollars: (r) => Number(r.expenseDollars),
      mileageDollars: (r) => Number(r.mileageDollars),
      total: (r) => Number(r.total),
    }),
    [],
  );
  const [sortedRows, sort, onSort] = useSortedRows(rows || [], plantSortConfig);
  if (!rows || rows.length === 0) {
    return (
      <div className="card">
        <p className="muted" style={{ marginTop: 0 }}>
          No plants have approved timesheets in this period.
        </p>
      </div>
    );
  }
  return (
    <div className="card" style={{ overflowX: 'auto' }}>
      <table className="mini-table" style={{ minWidth: 900 }}>
        <thead>
          <tr>
            <SortableTh
              sortKey="plantCode"
              currentSort={sort}
              onSort={onSort}
              width={110}
            >
              Plant
            </SortableTh>
            <SortableTh
              sortKey="suppliers"
              currentSort={sort}
              onSort={onSort}
            >
              Suppliers
            </SortableTh>
            <SortableTh
              sortKey="laborHours"
              currentSort={sort}
              onSort={onSort}
              align="right"
              width={110}
            >
              Total Hours
            </SortableTh>
            <SortableTh
              sortKey="expenseDollars"
              currentSort={sort}
              onSort={onSort}
              align="right"
              width={130}
            >
              Total Expenses
            </SortableTh>
            <SortableTh
              sortKey="mileageDollars"
              currentSort={sort}
              onSort={onSort}
              align="right"
              width={130}
            >
              Total Mileage
            </SortableTh>
            <SortableTh
              sortKey="total"
              currentSort={sort}
              onSort={onSort}
              align="right"
              width={130}
            >
              Amount Total
            </SortableTh>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r) => (
            <tr key={r.plantCode}>
              <td>
                <Link
                  to={`/plants/${encodeURIComponent(r.plantCode)}`}
                  style={{ textDecoration: 'none' }}
                >
                  <span className="badge plant">{r.plantCode}</span>
                </Link>
              </td>
              <td>{r.suppliers.join(', ') || '—'}</td>
              <td style={{ textAlign: 'right' }}>
                {r.laborHours}
                {r.orphanHours > 0 && (
                  <span
                    className="muted"
                    style={{ fontSize: 11, marginLeft: 6 }}
                    title={`${r.orphanHours} hrs not priced — no rate on file`}
                  >
                    (+{r.orphanHours} unpriced)
                  </span>
                )}
              </td>
              <td style={{ textAlign: 'right' }}>
                ${r.expenseDollars.toFixed(2)}
              </td>
              <td style={{ textAlign: 'right' }}>
                ${r.mileageDollars.toFixed(2)}
                {r.miles > 0 && (
                  <div className="muted" style={{ fontSize: 11 }}>
                    {r.miles} mi
                  </div>
                )}
              </td>
              <td
                style={{
                  textAlign: 'right',
                  fontWeight: 700,
                  color: 'var(--brand-primary, #E87722)',
                }}
              >
                ${r.total.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- By-regional-manager table -----------------------------------

function RegionalManagerBillingTable({ rows }) {
  const rmSortConfig = useMemo(
    () => ({
      regionalManager: (r) => r.regionalManager,
      submitters: (r) => (r.submitters || []).join(', '),
      plantCodes: (r) => (r.plantCodes || []).join(', '),
      laborHours: (r) => Number(r.laborHours) + Number(r.orphanHours || 0),
      expenseDollars: (r) => Number(r.expenseDollars),
      mileageDollars: (r) => Number(r.mileageDollars),
      total: (r) => Number(r.total),
    }),
    [],
  );
  const [sortedRows, sort, onSort] = useSortedRows(rows || [], rmSortConfig);
  if (!rows || rows.length === 0) {
    return (
      <div className="card">
        <p className="muted" style={{ marginTop: 0 }}>
          No Regional Managers oversee approved timesheets in this
          period.
        </p>
      </div>
    );
  }
  return (
    <div className="card" style={{ overflowX: 'auto' }}>
      <table className="mini-table" style={{ minWidth: 900 }}>
        <thead>
          <tr>
            <SortableTh
              sortKey="regionalManager"
              currentSort={sort}
              onSort={onSort}
            >
              Regional Manager
            </SortableTh>
            <SortableTh
              sortKey="submitters"
              currentSort={sort}
              onSort={onSort}
            >
              Team Members
            </SortableTh>
            <SortableTh
              sortKey="plantCodes"
              currentSort={sort}
              onSort={onSort}
            >
              Plants
            </SortableTh>
            <SortableTh
              sortKey="laborHours"
              currentSort={sort}
              onSort={onSort}
              align="right"
              width={110}
            >
              Total Hours
            </SortableTh>
            <SortableTh
              sortKey="expenseDollars"
              currentSort={sort}
              onSort={onSort}
              align="right"
              width={130}
            >
              Total Expenses
            </SortableTh>
            <SortableTh
              sortKey="mileageDollars"
              currentSort={sort}
              onSort={onSort}
              align="right"
              width={130}
            >
              Total Mileage
            </SortableTh>
            <SortableTh
              sortKey="total"
              currentSort={sort}
              onSort={onSort}
              align="right"
              width={130}
            >
              Amount Total
            </SortableTh>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r) => (
            <tr key={r.regionalManager}>
              <td style={{ fontWeight: 600 }}>{r.regionalManager}</td>
              <td>
                {r.submitters.length === 0 ? (
                  <span className="muted">—</span>
                ) : r.submitters.length <= 2 ? (
                  r.submitters.join(', ')
                ) : (
                  <>
                    {r.submitters.slice(0, 2).join(', ')}
                    <span className="muted">
                      {' '}
                      +{r.submitters.length - 2} more
                    </span>
                  </>
                )}
              </td>
              <td>
                {r.plantCodes.length === 0 ? (
                  <span className="muted">—</span>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      gap: 4,
                      flexWrap: 'wrap',
                    }}
                  >
                    {r.plantCodes.map((code) => (
                      <Link
                        key={code}
                        to={`/plants/${encodeURIComponent(code)}`}
                        style={{ textDecoration: 'none' }}
                      >
                        <span className="badge plant">{code}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </td>
              <td style={{ textAlign: 'right' }}>
                {r.laborHours}
                {r.orphanHours > 0 && (
                  <span
                    className="muted"
                    style={{ fontSize: 11, marginLeft: 6 }}
                    title={`${r.orphanHours} hrs not priced — no rate on file`}
                  >
                    (+{r.orphanHours} unpriced)
                  </span>
                )}
              </td>
              <td style={{ textAlign: 'right' }}>
                ${r.expenseDollars.toFixed(2)}
              </td>
              <td style={{ textAlign: 'right' }}>
                ${r.mileageDollars.toFixed(2)}
                {r.miles > 0 && (
                  <div className="muted" style={{ fontSize: 11 }}>
                    {r.miles} mi
                  </div>
                )}
              </td>
              <td
                style={{
                  textAlign: 'right',
                  fontWeight: 700,
                  color: 'var(--brand-primary, #E87722)',
                }}
              >
                ${r.total.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- Team billing table ------------------------------------------

function TeamBillingTable({ rows }) {
  // Rows are now rolled up per team member (suppliers + plants combined),
  // so sorting keys mirror the By Regional Manager table.
  const teamSortConfig = useMemo(
    () => ({
      submitterName: (r) => r.submitterName,
      supplierNames: (r) => (r.supplierNames || []).join(', '),
      plantCodes: (r) => (r.plantCodes || []).join(', '),
      laborHours: (r) => Number(r.laborHours) + Number(r.orphanHours || 0),
      expenseDollars: (r) => Number(r.expenseDollars),
      mileageDollars: (r) => Number(r.mileageDollars),
      total: (r) => Number(r.total),
    }),
    [],
  );
  const [sortedRows, sort, onSort] = useSortedRows(rows || [], teamSortConfig);
  if (!rows || rows.length === 0) {
    return (
      <div className="card">
        <p className="muted" style={{ marginTop: 0 }}>
          No team members have approved timesheets in this period.
        </p>
      </div>
    );
  }
  return (
    <div className="card" style={{ overflowX: 'auto' }}>
      <table className="mini-table" style={{ minWidth: 900 }}>
        <thead>
          <tr>
            <SortableTh
              sortKey="submitterName"
              currentSort={sort}
              onSort={onSort}
            >
              Name
            </SortableTh>
            <SortableTh
              sortKey="supplierNames"
              currentSort={sort}
              onSort={onSort}
            >
              Suppliers
            </SortableTh>
            <SortableTh
              sortKey="plantCodes"
              currentSort={sort}
              onSort={onSort}
            >
              Plants
            </SortableTh>
            <SortableTh
              sortKey="laborHours"
              currentSort={sort}
              onSort={onSort}
              align="right"
              width={110}
            >
              Total Hours
            </SortableTh>
            <SortableTh
              sortKey="expenseDollars"
              currentSort={sort}
              onSort={onSort}
              align="right"
              width={130}
            >
              Total Expenses
            </SortableTh>
            <SortableTh
              sortKey="mileageDollars"
              currentSort={sort}
              onSort={onSort}
              align="right"
              width={130}
            >
              Total Mileage
            </SortableTh>
            <SortableTh
              sortKey="total"
              currentSort={sort}
              onSort={onSort}
              align="right"
              width={130}
            >
              Amount Total
            </SortableTh>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r) => (
            <tr key={r.submitterKey}>
              <td style={{ fontWeight: 600 }}>{r.submitterName}</td>
              <td>
                {r.supplierNames.length === 0 ? (
                  <span className="muted">—</span>
                ) : r.supplierNames.length <= 2 ? (
                  r.supplierNames.join(', ')
                ) : (
                  <>
                    {r.supplierNames.slice(0, 2).join(', ')}
                    <span className="muted">
                      {' '}
                      +{r.supplierNames.length - 2} more
                    </span>
                  </>
                )}
              </td>
              <td>
                {r.plantCodes.length === 0 ? (
                  <span className="muted">—</span>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      gap: 4,
                      flexWrap: 'wrap',
                    }}
                  >
                    {r.plantCodes.map((code) => (
                      <Link
                        key={code}
                        to={`/plants/${encodeURIComponent(code)}`}
                        style={{ textDecoration: 'none' }}
                      >
                        <span className="badge plant">{code}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </td>
              <td style={{ textAlign: 'right' }}>
                {r.laborHours}
                {r.orphanHours > 0 && (
                  <span
                    className="muted"
                    style={{ fontSize: 11, marginLeft: 6 }}
                    title={`${r.orphanHours} hrs not priced — no rate on file`}
                  >
                    (+{r.orphanHours} unpriced)
                  </span>
                )}
              </td>
              <td style={{ textAlign: 'right' }}>
                ${r.expenseDollars.toFixed(2)}
              </td>
              <td style={{ textAlign: 'right' }}>
                ${r.mileageDollars.toFixed(2)}
                {r.miles > 0 && (
                  <div className="muted" style={{ fontSize: 11 }}>
                    {r.miles} mi
                  </div>
                )}
              </td>
              <td
                style={{
                  textAlign: 'right',
                  fontWeight: 700,
                  color: 'var(--brand-primary, #E87722)',
                }}
              >
                ${r.total.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Thousands-separated formatters for the Totals tiles so large numbers
// read easily (e.g. "$15,457.40", "12,345.6"). fmtMoney keeps 2 decimals
// like the old toFixed(2); fmtNum drops trailing zeros for hours/miles.
function fmtMoney(value) {
  const n = Number(value) || 0;
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
function fmtNum(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/**
 * Headline tile for the Totals row — label on top, big value, muted
 * detail line underneath. By default the tile is plain (white card,
 * ink-colored value); pass `highlighted` to switch on the orange
 * treatment for the one tile that should draw the eye first.
 */
function TotalTile({ label, value, unit, detail, highlighted }) {
  // Shrink the value font as the number gets longer so a comma-formatted
  // total (e.g. "$1,234,567.89") still fits inside the tile.
  const valLen = String(value).length;
  const valueFontSize = valLen > 11 ? 22 : valLen > 8 ? 25 : 28;
  // When highlighted, the tile is solid Proxemics orange with white
  // text throughout — label, value, and detail line all flip to white
  // so the whole card reads as a single bold "headline" block.
  const cardStyle = highlighted
    ? {
        background: 'var(--brand-primary)',
        border: '1px solid var(--brand-primary)',
        marginTop: 0,
      }
    : { marginTop: 0 };
  const labelColor = highlighted
    ? 'rgba(255, 255, 255, 0.85)'
    : 'var(--brand-muted)';
  const valueColor = highlighted ? '#ffffff' : 'var(--brand-ink)';
  const detailColor = highlighted
    ? 'rgba(255, 255, 255, 0.85)'
    : 'var(--brand-muted)';
  return (
    <div className="card" style={cardStyle}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: labelColor,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: valueFontSize,
          fontWeight: 700,
          color: valueColor,
          marginTop: 4,
        }}
      >
        {value}
        {unit && (
          <span
            style={{
              fontSize: 16,
              fontWeight: 600,
              marginLeft: 6,
              color: valueColor,
            }}
          >
            {unit}
          </span>
        )}
      </div>
      {detail && (
        <div style={{ fontSize: 12, marginTop: 6, color: detailColor }}>
          {detail}
        </div>
      )}
    </div>
  );
}

/**
 * Count the unique plants that appear on any supplier's breakdown in
 * this period. A plant tagged by two suppliers still counts once.
 */
function countUniquePlants(billing) {
  const seen = new Set();
  for (const s of billing.suppliers || []) {
    for (const p of s.plants || []) {
      if (p.plantCode) seen.add(p.plantCode);
    }
  }
  return seen.size;
}

function hoursTotalDetail(billing) {
  // Count unique submitters in the team breakdown so the tile under
  // Hours Total reads something like "5 team members". Falls back to
  // empty when the period has no data.
  const uniqueSubmitters = new Set(
    (billing.team || []).map((r) => r.submitterKey),
  );
  if (uniqueSubmitters.size === 0) return '';
  return `${uniqueSubmitters.size} team member${
    uniqueSubmitters.size === 1 ? '' : 's'
  }`;
}

function periodTotalDetail(billing) {
  if (billing.suppliers.length === 0) return '';
  // Count DISTINCT plants actually billed — not (supplier × plant) pairs.
  // The same plant billed under several suppliers is still one plant (per
  // Jillian, Aug 2026: it was over-counting, e.g. "45 plants" when only 3
  // were billed).
  const plantCodes = new Set();
  for (const s of billing.suppliers) {
    for (const p of s.plants || []) {
      const code = String(p.plantCode || '').trim().toUpperCase();
      if (code) plantCodes.add(code);
    }
  }
  const plantCount = plantCodes.size;
  return `${billing.suppliers.length} supplier${
    billing.suppliers.length === 1 ? '' : 's'
  } · ${plantCount} plant${plantCount === 1 ? '' : 's'}`;
}

/**
 * Section heading used between Billing sections. Muted uppercase,
 * matches the Dashboard "By the Numbers" / "Plant Locations" style.
 */
function SectionHeading({ children, flush = false }) {
  return (
    <h2
      style={{
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'var(--brand-muted, #64748b)',
        // `flush` drops the bottom margin + line-height so the heading sits
        // on the same baseline as the collapse chevron next to it.
        margin: flush ? 0 : '0 0 10px',
        lineHeight: flush ? 1 : undefined,
      }}
    >
      {children}
    </h2>
  );
}

/**
 * Collapsible billing subsection (By Supplier / By Plant / By Regional
 * Manager / By Team Member). Collapsed by default — the Director clicks the
 * heading to reveal the table (per Jillian, Aug 2026), so the page lands
 * compact and they open only the view they need.
 */
function CollapsibleSection({ title, children, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <div style={{ marginTop: 24 }}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          color: 'inherit',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 10,
            fontSize: 12,
            lineHeight: 1,
            color: 'var(--brand-muted, #64748b)',
            transition: 'transform 0.15s ease',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        >
          ▶
        </span>
        <SectionHeading flush>{title}</SectionHeading>
      </button>
      {expanded && <div>{children}</div>}
    </div>
  );
}

// ---- Per-supplier card --------------------------------------------

function SupplierBillingCard({ supplier }) {
  return (
    <div className="card">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            <Link
              to={`/suppliers/${encodeURIComponent(supplier.supplierCode)}`}
              style={{ color: 'var(--brand-ink)', textDecoration: 'none' }}
            >
              {supplier.supplierName}
            </Link>
          </div>
          <div
            className="muted"
            style={{ fontSize: 13, marginTop: 2 }}
          >
            {supplier.supplierCode} · {supplier.plants.length} plant
            {supplier.plants.length === 1 ? '' : 's'}
          </div>
        </div>
        <div
          style={{
            textAlign: 'right',
            fontSize: 22,
            fontWeight: 700,
            color: 'var(--brand-primary, #E87722)',
          }}
        >
          ${supplier.supplierTotal.toFixed(2)}
        </div>
      </div>

      <div
        style={{
          marginTop: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {supplier.plants.map((p) => (
          <PlantBillingRow
            key={p.plantCode}
            supplierCode={supplier.supplierCode}
            plant={p}
          />
        ))}
      </div>
    </div>
  );
}

// ---- Per-plant row inside a supplier card ------------------------

function PlantBillingRow({ supplierCode, plant }) {
  // Labor is now shown as one line per coverage type (Standard Liaison
  // Hours / OT Liaison Hours / …), each with its own effective rate, so
  // OT no longer blends into the Standard total (per Jillian, Aug 2026).
  const laborLines =
    plant.laborLines && plant.laborLines.length > 0
      ? plant.laborLines
      : plant.laborHours > 0
        ? [
            {
              coverageType: 'Standard',
              hours: plant.laborHours,
              dollars: plant.laborDollars,
              rate:
                plant.laborHours > 0
                  ? Math.round((plant.laborDollars / plant.laborHours) * 100) /
                    100
                  : 0,
            },
          ]
        : [];

  return (
    <div
      style={{
        padding: '12px 14px',
        borderRadius: 8,
        background: '#f8fafc',
        border: '1px solid var(--brand-border, #e5e7eb)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Link
            to={`/plants/${encodeURIComponent(plant.plantCode)}`}
            style={{ textDecoration: 'none' }}
          >
            <span className="badge plant">{plant.plantCode}</span>
          </Link>
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: 'var(--brand-ink)',
          }}
        >
          ${plant.total.toFixed(2)}
        </div>
      </div>

      <div
        style={{
          marginTop: 10,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 10,
          fontSize: 13,
        }}
      >
        {laborLines.map((line) => (
          <LineItem
            key={line.coverageType}
            label={COVERAGE_TYPE_LABELS[line.coverageType] || line.coverageType}
            detail={`${line.hours} hrs × $${line.rate.toFixed(2)}/hr`}
            value={`$${line.dollars.toFixed(2)}`}
          />
        ))}
        {plant.miles > 0 && (
          <LineItem
            label="Mileage"
            detail={`${plant.miles} miles`}
            value={`$${plant.mileageDollars.toFixed(2)}`}
          />
        )}
        {plant.expenseDollars > 0 && (
          <LineItem
            label="Expenses"
            detail=""
            value={`$${plant.expenseDollars.toFixed(2)}`}
          />
        )}
        {plant.orphanHours > 0 && (
          <LineItem
            label="Unpriced Hours"
            detail={`${plant.orphanHours} hrs — no rate on file`}
            value="—"
            warning
          />
        )}
      </div>
    </div>
  );
}

/**
 * Expanded Labor line that shows the per-rate breakdown when the rate
 * changed mid-period. Each rate gets its own "hrs × $/hr = $" line so
 * the director can see exactly how the dollar total was reached.
 */
function RateBreakdownBlock({ plant, rateBreakdown }) {
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color: 'var(--brand-muted)',
        }}
      >
        Liaison Support
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: 'var(--brand-ink)',
          marginTop: 2,
        }}
      >
        ${plant.laborDollars.toFixed(2)}
      </div>
      <div
        className="muted"
        style={{
          fontSize: 12,
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <div style={{ fontSize: 11, fontStyle: 'italic' }}>
          Rate changed during the period — breakdown:
        </div>
        {rateBreakdown.map((r) => (
          <div key={r.rate}>
            {r.hours} hrs × ${r.rate.toFixed(2)}/hr = ${r.dollars.toFixed(2)}
          </div>
        ))}
        <div style={{ fontSize: 11, marginTop: 2 }}>
          Total: {plant.laborHours} hrs = ${plant.laborDollars.toFixed(2)}
        </div>
      </div>
    </div>
  );
}

// ---- Small components --------------------------------------------

function LineItem({ label, detail, value, warning }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color: warning
            ? 'var(--brand-warning, #b45309)'
            : 'var(--brand-muted)',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: warning
            ? 'var(--brand-warning, #b45309)'
            : 'var(--brand-ink)',
          marginTop: 2,
        }}
      >
        {value}
      </div>
      {detail && (
        <div
          className="muted"
          style={{ fontSize: 12, marginTop: 2 }}
        >
          {detail}
        </div>
      )}
    </div>
  );
}

function PresetButton({ label, onClick, active = false }) {
  // Selected preset renders in the brand-orange primary style (white
  // text on orange) so the active range is obvious at a glance —
  // mirrors the same pattern used on Timesheet Approvals. Inactive
  // presets keep the neutral outline treatment.
  return (
    <button
      type="button"
      className={active ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
      aria-pressed={active}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

// ---- Helpers ------------------------------------------------------

/**
 * Sum approved LABOR hours per plant across every timesheet day that
 * falls inside [startIso, endIso] (both inclusive). Mileage and
 * Expenses rows don't count — this powers the unusual-hours check,
 * which is about coverage hours only. Returns Map<plantCode, hours>.
 */
function plantLaborHoursInWindow(timesheets, startIso, endIso) {
  const map = new Map();
  for (const t of timesheets || []) {
    if ((t.Status || '') !== 'Approved') continue;
    const ws = (t.WeekStart || '').slice(0, 10);
    if (!ws) continue;
    const rows = Array.isArray(t.rows) ? t.rows : [];
    for (const r of rows) {
      const coverageType = r.coverageType || 'Standard';
      if (coverageType === 'Mileage' || coverageType === 'Expenses') continue;
      const plantCode = String(r.plantCode || '').trim().toUpperCase();
      if (!plantCode) continue;
      const values = r.values || {};
      for (let i = 0; i < DAY_KEYS.length; i += 1) {
        const v = Number(values[DAY_KEYS[i]]) || 0;
        if (v <= 0) continue;
        const dayIso = addDaysIso(ws, i);
        if (startIso && dayIso < startIso) continue;
        if (endIso && dayIso > endIso) continue;
        map.set(plantCode, (map.get(plantCode) || 0) + v);
      }
    }
  }
  return map;
}

// Timezone-safe ISO day arithmetic (parses components rather than the
// string so negative-offset timezones don't shift the day).
function addDaysIso(iso, days) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
}

function daysBetweenIso(aIso, bIso) {
  const parse = (iso) => {
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
  };
  const a = parse(aIso);
  const b = parse(bIso);
  if (!a || !b) return 0;
  return Math.round((b - a) / 86400000);
}

/**
 * Render a team row's submission date cell. A single timesheet feeding
 * the row shows one date; multiple timesheets show an earliest–latest
 * range with a small "(N)" count underneath so the approver knows how
 * many distinct submissions are rolled up.
 */
function formatSubmittedRange(earliestIso, latestIso, count) {
  if (!earliestIso && !latestIso) {
    return <span className="muted">—</span>;
  }
  const same = !latestIso || earliestIso === latestIso;
  if (same) {
    return <div>{formatShortDate(earliestIso)}</div>;
  }
  return (
    <div>
      <div>
        {formatShortDate(earliestIso)} – {formatShortDate(latestIso)}
      </div>
      <div className="muted" style={{ fontSize: 11 }}>
        {count} timesheets
      </div>
    </div>
  );
}

function formatShortDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  // MM/DD/YYYY — picked over toLocaleDateString to avoid the
  // browser-locale "Jan 5, 2026" expansion that ate column width on
  // the Billing dashboard. Pad single-digit month/day so widths line
  // up cleanly across rows.
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function formatRangeLabel(startIso, endIso) {
  if (!startIso || !endIso) return '';
  const fmt = (iso) => {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return iso;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  return `${fmt(startIso)} — ${fmt(endIso)}`;
}

